const API_BASE = 'https://api.spotify.com/v1';

export class SpotifyApiError extends Error {
  readonly status: number;
  readonly retryAfterSeconds: number | null;

  constructor(status: number, message: string, retryAfterSeconds: number | null = null) {
    super(message);
    this.name = 'SpotifyApiError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Kein aktives Spotify-Connect-Geraet verfuegbar. */
  get isNoActiveDevice(): boolean {
    return this.status === 404;
  }

  /** Spotify Premium fehlt oder Aktion nicht erlaubt. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  /** Interner Zaehler fuer Retries – nicht von aussen setzen. */
  attempt?: number;
}

type TokenProvider = () => Promise<string>;

const MAX_RETRIES = 2;
const MAX_RETRY_AFTER_SECONDS = 30;

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${API_BASE}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Duenner Wrapper um die Spotify Web API.
 * Kuemmert sich um Token, 401-Retry (Token-Refresh) und 429-Backoff.
 */
export class SpotifyClient {
  #getToken: TokenProvider;

  constructor(getToken: TokenProvider) {
    this.#getToken = getToken;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T | null> {
    const { method = 'GET', query, body, attempt = 0 } = options;
    const token = await this.#getToken();

    const response = await fetch(buildUrl(path, query), {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    if (response.status === 204 || response.status === 202) return null;

    if (response.status === 429 && attempt < MAX_RETRIES) {
      const header = Number(response.headers.get('Retry-After'));
      const waitSeconds = Number.isFinite(header) && header > 0 ? header : 1;
      if (waitSeconds <= MAX_RETRY_AFTER_SECONDS) {
        await delay(waitSeconds * 1000);
        return this.request<T>(path, { ...options, attempt: attempt + 1 });
      }
    }

    if (response.status === 401 && attempt < 1) {
      // Token koennte gerade abgelaufen sein – getToken erneuert es.
      return this.request<T>(path, { ...options, attempt: attempt + 1 });
    }

    if (!response.ok) {
      const detail = await response
        .json()
        .then((payload: { error?: { message?: string } }) => payload?.error?.message)
        .catch(() => undefined);
      const retryAfter = Number(response.headers.get('Retry-After'));
      throw new SpotifyApiError(
        response.status,
        detail ?? `Spotify-Fehler ${response.status}`,
        Number.isFinite(retryAfter) ? retryAfter : null,
      );
    }

    // Steuerbefehle wie seek/play antworten teils mit leerem oder nicht-JSON
    // Body. Ohne diese Pruefung wirft JSON.parse und der Befehl gilt faelschlich
    // als fehlgeschlagen, obwohl Spotify ihn ausgefuehrt hat.
    const contentType = response.headers.get('Content-Type') ?? '';
    if (!contentType.includes('application/json')) return null;

    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as T;
    } catch {
      return null;
    }
  }

  get<T>(path: string, query?: RequestOptions['query']): Promise<T | null> {
    return this.request<T>(path, { method: 'GET', query });
  }

  put<T = null>(path: string, options: Omit<RequestOptions, 'method'> = {}): Promise<T | null> {
    return this.request<T>(path, { ...options, method: 'PUT' });
  }

  post<T = null>(path: string, options: Omit<RequestOptions, 'method'> = {}): Promise<T | null> {
    return this.request<T>(path, { ...options, method: 'POST' });
  }
}
